import { CommissionStatuses } from 'constants/commissionStatuses';
import { CommissionTypes } from 'constants/commissionTypes';
import { Resources } from 'constants/resources';
import { Role } from 'constants/roles';

import { Skeleton } from '@mui/material';
import { useCommissionsAvailableActions } from 'hooks/queries/useCommissionsAvailableActions';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRouteTabs } from 'hooks/useRouteTabs';
import { CommissionsDeleteDialog } from 'pages/Commissions/components/CommissionsDeleteDialog';
import { CommissionsNewVersionButton } from 'pages/Commissions/components/CommissionsNewVersionButton';
import { useShowController } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
} from 'shared/mui/ContextMenu';
import { FunctionField } from 'shared/react-admin/Fields';
import { RelatedCommissionsLink } from 'shared/react-admin/Links/RelatedCommissionsLink/RelatedCommissionsLink';
import { ShowPage } from 'shared/react-admin/Pages/ShowPage/ShowPage';
import { CommissionsAgreeDialog } from 'shared/widgets/CommissionsAgreeDialog/CommissionsAgreeDialog';
import { CommissionsArchivedDialog } from 'shared/widgets/CommissionsArchivedDialog/CommissionsArchivedDialog';
import { CommissionsArchivedErrorDialog } from 'shared/widgets/CommissionsArchivedErrorDialog/CommissionsArchivedErrorDialog';
import { CommissionsByAgreementDialog } from 'shared/widgets/CommissionsByAgreementDialog/CommissionsByAgreementDialog';
import { CommissionsCancelDialog } from 'shared/widgets/CommissionsCancelDialog/CommissionsCancelDialog';
import { CommissionsImplementDialog } from 'shared/widgets/CommissionsImplementDialog/CommissionsImplementDialog';
import { CommissionsImplementErrorDialog } from 'shared/widgets/CommissionsImplementErrorDialog/CommissionsImplementErrorDialog';
import { CommissionsRejectDialog } from 'shared/widgets/CommissionsRejectDialog/CommissionsRejectDialog';
import { CommissionsShowChipFields } from 'shared/widgets/CommissionsShowChipFields/CommissionsShowChipFields';
import { CommissionsTabs } from 'shared/widgets/CommissionsTabs/CommissionsTabs';
import { Commission } from 'types/embedded/commission';
import { CommissionRecalculationTaskStatus } from 'types/embedded/commissionRecalculationTask';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';

import { CommissionsShowSkeleton } from './CommissionsShowSkeleton';

export const CommissionsShow: React.FC = () => {
  const { hasResourcePermissions, hasResourceAccess, roles, hasAnyRole } = usePermissions();
  const { record, isFetching, isPending } = useShowController<Commission>();
  const { data: availableActions = [], isLoading: isLoadingAvailableActions } =
    useCommissionsAvailableActions(record?.originId);
  const { currentTab, handleTabChange } = useRouteTabs();

  const {
    handleOpenRecordModal: handleOpenModalDelete,
    openRecordModal: openModalDelete,
    handleCloseRecordModal: handleCloseModalDelete,
  } = useRecordModal<Commission>();

  const {
    record: recordModalAgreement,
    handleOpenRecordModal: handleOpenModalAgreement,
    openRecordModal: openModalAgreement,
    handleCloseRecordModal: handleCloseModalAgreement,
  } = useRecordModal<Commission>();

  const {
    record: recordModalAgree,
    handleOpenRecordModal: handleOpenModalAgree,
    openRecordModal: openModalAgree,
    handleCloseRecordModal: handleCloseModalAgree,
  } = useRecordModal<Commission>();

  const {
    record: recordModalReject,
    handleOpenRecordModal: handleOpenModalReject,
    openRecordModal: openModalReject,
    handleCloseRecordModal: handleCloseModalReject,
  } = useRecordModal<Commission>();

  const {
    record: recordModalImplement,
    handleOpenRecordModal: handleOpenModalImplement,
    openRecordModal: openModalImplement,
    handleCloseRecordModal: handleCloseModalImplement,
  } = useRecordModal<Commission>();

  const {
    record: recordModalCancel,
    handleOpenRecordModal: handleOpenModalCancel,
    openRecordModal: openModalCancel,
    handleCloseRecordModal: handleCloseModalCancel,
  } = useRecordModal<Commission>();

  const {
    record: recordModalArchived,
    handleOpenRecordModal: handleOpenModalArchived,
    openRecordModal: openModalArchived,
    handleCloseRecordModal: handleCloseModalArchived,
  } = useRecordModal<Commission>();

  const {
    record: recordModalImplementError,
    handleOpenRecordModal: handleOpenModalImplementError,
    openRecordModal: openModalImplementError,
    handleCloseRecordModal: handleCloseModalImplementError,
  } = useRecordModal<Commission>();

  const {
    record: recordModalArchivedError,
    handleOpenRecordModal: handleOpenModalArchivedError,
    openRecordModal: openModalArchivedError,
    handleCloseRecordModal: handleCloseModalArchivedError,
  } = useRecordModal<Commission>();

  const status = record?.status?.value as CommissionStatuses;
  const tasks = record?.recalculationTasks || [];

  const renderActions = (): React.ReactElement | React.ReactElement[] | false => {
    if (isLoadingAvailableActions) {
      return <Skeleton height={36} variant="rounded" width={240} />;
    }

    const actions = [];

    if (
      !availableActions.includes(CommissionStatuses.Implemented) &&
      !availableActions.includes(CommissionStatuses.Archived) &&
      status === CommissionStatuses.Implemented &&
      hasAnyRole([Role.Admin, Role.Tech, Role.Payment])
    ) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label="Архивировать"
          onClick={() => handleOpenModalArchivedError(record)}
          variant="outlined"
        />,
      );
    }

    if (
      !availableActions.includes(CommissionStatuses.Archived) &&
      ![
        CommissionStatuses.Created,
        CommissionStatuses.OnApproval,
        CommissionStatuses.Rejected,
        CommissionStatuses.Approved,
        CommissionStatuses.Cancelled,
        CommissionStatuses.Implemented,
        CommissionStatuses.Archived,
      ].includes(status) &&
      hasAnyRole([Role.Payment])
    ) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label="Архивировать"
          onClick={() => handleOpenModalArchivedError(record)}
          variant="outlined"
        />,
      );
    }

    if (availableActions.includes(CommissionStatuses.Rejected)) {
      actions.push(
        <Button
          color="error"
          key="btn-reject"
          label="Отклонить"
          onClick={() => handleOpenModalReject(record)}
          variant="outlined"
        />,
      );
    }

    if (
      !availableActions.includes(CommissionStatuses.Implemented) &&
      status === CommissionStatuses.Approved &&
      !record?.parent &&
      hasAnyRole([Role.Admin, Role.Tech])
    ) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement-ex"
          label="Внедрить"
          onClick={() => handleOpenModalImplementError(record)}
          variant="contained"
        />,
      );
    }

    if (
      !availableActions.includes(CommissionStatuses.Implemented) &&
      status === CommissionStatuses.Approved &&
      hasAnyRole([Role.Payment])
    ) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement-ex"
          label="Внедрить"
          onClick={() => handleOpenModalImplementError(record)}
          variant="contained"
        />,
      );
    }

    if (availableActions.includes(CommissionStatuses.Cancelled)) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label="Аннулировать"
          onClick={() => handleOpenModalCancel(record)}
          variant="outlined"
        />,
      );
    }

    if (availableActions.includes(CommissionStatuses.Archived)) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label="Архивировать"
          onClick={() => handleOpenModalArchived(record)}
          variant="outlined"
        />,
      );
    }

    if (availableActions.includes(CommissionStatuses.CreateChildAvailable)) {
      actions.push(
        <CommissionsNewVersionButton
          key="btn-new-version"
          onClick={() => handleOpenModalImplement(record)}
          pathname="/commissions/create-new"
        />,
      );
    }

    if (availableActions.includes(CommissionStatuses.OnApproval)) {
      actions.push(
        <Button
          color="primary"
          key="btn-agreement"
          label="На согласование"
          onClick={() => handleOpenModalAgreement(record)}
          variant="contained"
        />,
      );
    }

    if (availableActions.includes(CommissionStatuses.Approved)) {
      actions.push(
        <Button
          color="primary"
          key="btn-agree"
          label="Согласовать"
          onClick={() => handleOpenModalAgree(record)}
          variant="contained"
        />,
      );
    }

    if (
      availableActions.includes(CommissionStatuses.Implemented) &&
      !(
        record?.parent &&
        status === CommissionStatuses.Approved &&
        tasks.some((task) => getId(task.status) === CommissionRecalculationTaskStatus.Created)
      )
    ) {
      actions.push(
        <Button
          color="primary"
          key="btn-implement"
          label="Внедрить"
          onClick={() => handleOpenModalImplement(record)}
          variant="contained"
        />,
      );
    }

    return actions;
  };

  const isAdmin = roles.includes(Role.Admin);

  const isShowButton =
    !!status &&
    [CommissionStatuses.Created, CommissionStatuses.Rejected].includes(status) &&
    record?.typeEnum?.value !== CommissionTypes.Ladder;

  const isImplementedStatus = !isAdmin && status === CommissionStatuses.Implemented;

  const isApprovedStatus =
    !isAdmin &&
    record?.typeEnum?.value === CommissionTypes.Regular &&
    status === CommissionStatuses.Approved;

  return (
    <ShowPage
      actions={
        <ContextMenu key="context-menu-button">
          {hasResourceAccess(Resources.UserActions) && (
            <ContextMenuEntityUserActionsButton entityName="commission" />
          )}
          {isShowButton &&
            hasResourcePermissions(Resources.Commissions, [Permission.Full, Permission.Delete]) && (
              <ContextMenuEditButton />
            )}
          {isShowButton &&
            hasResourcePermissions(Resources.Commissions, [Permission.Full, Permission.Delete]) && (
              <ContextMenuDeleteButton onClick={() => handleOpenModalDelete()} />
            )}
        </ContextMenu>
      }
      cardActionsButtonsProps={{ sx: { minHeight: '84px' } }}
      listName="Комиссии"
      rightActionsSlot={renderActions()}
      skeleton={isPending && <CommissionsShowSkeleton />}
      title="Просмотр комиссии"
    >
      {isFetching ? (
        <CommissionsShowSkeleton />
      ) : (
        <>
          <CommissionsShowChipFields />
          <CommissionsTabs
            handleTabChange={handleTabChange}
            initialTab={currentTab}
            relatedField={
              <FunctionField
                render={(record: Commission) => {
                  return <RelatedCommissionsLink record={record} />;
                }}
              />
            }
          />
        </>
      )}
      {isApprovedStatus && (
        <Box pt={3}>
          <Alert severity="info">Для внедрения комиссии обратитесь к администратору.</Alert>
        </Box>
      )}
      {isImplementedStatus && (
        <Box pt={3}>
          <Alert severity="info">
            Для архивирования данной комиссии обратитесь к администратору или создайте и внедрите
            новую дочернюю комиссию.
          </Alert>
        </Box>
      )}
      {openModalDelete && (
        <CommissionsDeleteDialog
          onClose={handleCloseModalDelete}
          open={openModalDelete}
          record={record}
        />
      )}
      <CommissionsByAgreementDialog
        onClose={handleCloseModalAgreement}
        open={openModalAgreement}
        record={recordModalAgreement}
      />
      <CommissionsAgreeDialog
        onClose={handleCloseModalAgree}
        open={openModalAgree}
        record={recordModalAgree}
      />
      <CommissionsRejectDialog
        onClose={handleCloseModalReject}
        open={openModalReject}
        record={recordModalReject}
      />
      <CommissionsImplementDialog
        onClose={handleCloseModalImplement}
        open={openModalImplement}
        record={recordModalImplement}
      />
      <CommissionsCancelDialog
        onClose={handleCloseModalCancel}
        open={openModalCancel}
        record={recordModalCancel}
      />
      <CommissionsArchivedDialog
        onClose={handleCloseModalArchived}
        open={openModalArchived}
        record={recordModalArchived}
      />
      <CommissionsImplementErrorDialog
        onClose={handleCloseModalImplementError}
        open={openModalImplementError}
        record={recordModalImplementError}
      />
      <CommissionsArchivedErrorDialog
        onClose={handleCloseModalArchivedError}
        open={openModalArchivedError}
        record={recordModalArchivedError}
      />
    </ShowPage>
  );
};
